import { InAppPrimaryContent } from 'modules/campaigns/editor/types';
import { WebInAppDisplaySettingsDefinition } from 'modules/graphql/types';
import { FC } from 'react';
import styled from 'styled-components';
import {
  DesktopBottom,
  DesktopCenter,
  DesktopFull,
  DesktopTop,
  PhoneBottom,
  PhoneCenter,
  PhoneFull,
  PhoneTop,
  SvgIconProps,
} from 'ui/icons';
import { spacing, colors } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { CampaignFormField, ReadOnlyField } from '../../FormFields';

import { RawJsonViewer } from './RawJsonViewer';
import { useTemplateContext } from '../../../context';
import { LightText } from '../Sections.styled';

export const BG_COLOR_TEST_IDS = {
  OVERLAY: 'background-overlay-ro',
  COLOR_REVIEW: 'background-overlay-color-review',
  COLOR_DETAILS: 'background-overlay-color-details',
};

type ColorPreviewProps = {
  $hexColor: string;
  $alpha: number;
};

const ColorPreview = styled.div<ColorPreviewProps>`
  width: ${pxToRem(20)};
  height: ${pxToRem(20)};
  background: ${(props) => props.$hexColor};
  border-radius: ${spacing.quarter};
  opacity: ${(props) => props.$alpha};
  margin: auto;
`;

const BgOverlayContainer = styled.div`
  gap: 5px;
  display: flex;
  align-items: center;
`;

export const formatOpacityForUI = (opacity?: number) =>
  opacity !== undefined ? `${Math.round(opacity * 100)}` : '';

type InAppPositionMapperItem = {
  MobileIcon: FC<React.PropsWithChildren<SvgIconProps>>;
  DesktopIcon: FC<React.PropsWithChildren<SvgIconProps>>;
  displayName: string;
};

const generateInAppPositionItem = (
  mobileIcon: InAppPositionMapperItem['MobileIcon'],
  desktopIcon: InAppPositionMapperItem['DesktopIcon'],
  displayName: InAppPositionMapperItem['displayName'],
): InAppPositionMapperItem => ({
  MobileIcon: mobileIcon,
  DesktopIcon: desktopIcon,
  displayName,
});

const inAppPositionMapper: {
  [key in WebInAppDisplaySettingsDefinition]: InAppPositionMapperItem;
} = {
  [WebInAppDisplaySettingsDefinition.Full]: generateInAppPositionItem(
    PhoneFull,
    DesktopFull,
    'Full',
  ),
  [WebInAppDisplaySettingsDefinition.BottomRight]: generateInAppPositionItem(
    PhoneBottom,
    DesktopBottom,
    'Bottom',
  ),
  [WebInAppDisplaySettingsDefinition.Center]: generateInAppPositionItem(
    PhoneCenter,
    DesktopCenter,
    'Center',
  ),
  [WebInAppDisplaySettingsDefinition.TopRight]: generateInAppPositionItem(
    PhoneTop,
    DesktopTop,
    'Top',
  ),
};

export const InAppBehavior = () => {
  const {
    templateAndContent: { primaryContent },
  } = useTemplateContext();
  const { webInAppDisplaySettings, payload, inAppDisplaySettings } =
    primaryContent as InAppPrimaryContent;

  const { bgColor, shouldAnimate } = inAppDisplaySettings || {};

  const positionContent =
    inAppPositionMapper?.[webInAppDisplaySettings as WebInAppDisplaySettingsDefinition];
  return (
    <>
      <CampaignFormField labelText="Position">
        <ReadOnlyField name="webInAppDisplaySettings">
          {positionContent ? (
            <>
              <positionContent.MobileIcon
                data-test={`mobile-icon-${webInAppDisplaySettings}`}
                color={colors.grey50}
              />
              <positionContent.DesktopIcon
                data-test={`desktop-icon-${webInAppDisplaySettings}`}
                color={colors.grey50}
              />
              <span data-test={`position-name-${webInAppDisplaySettings}`}>
                {positionContent.displayName}
              </span>
            </>
          ) : (
            <LightText>Not set</LightText>
          )}
        </ReadOnlyField>
      </CampaignFormField>

      <CampaignFormField labelText="Preset animations">
        <ReadOnlyField name="shouldAnimate">
          {shouldAnimate ? <>On</> : <LightText>Off</LightText>}
        </ReadOnlyField>
      </CampaignFormField>

      <CampaignFormField labelText="Background overlay">
        <ReadOnlyField name="bgColor">
          {bgColor ? (
            <BgOverlayContainer data-test={BG_COLOR_TEST_IDS.OVERLAY}>
              <ColorPreview
                data-test={BG_COLOR_TEST_IDS.COLOR_REVIEW}
                $alpha={bgColor?.alpha}
                $hexColor={bgColor?.hex}
              />
              <span data-test={BG_COLOR_TEST_IDS.COLOR_DETAILS}>
                {`${bgColor.hex}`} ({formatOpacityForUI(bgColor?.alpha)}%)
              </span>
            </BgOverlayContainer>
          ) : (
            <LightText>Not set</LightText>
          )}
        </ReadOnlyField>
      </CampaignFormField>
      <RawJsonViewer payload={payload} />
    </>
  );
};
